import './LinkDialog.scss';
import { Button, Form, Input, Popover } from '../..';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputRef } from 'antd';
import { createPortal } from 'react-dom';
import { faBan, faCheck } from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import validator from 'validator';

interface LinkDialogProps {
    isOpen: boolean;
    text: string;
    url: string;
    onSave: (link: { text: string; url: string }) => void;
    onVisibleChange: (visible: boolean) => void;
    showArrow: boolean;
    linkRef?: HTMLDivElement | null;
}

export const LinkDialog = ({ isOpen, text, url, onSave, showArrow, onVisibleChange, linkRef }: LinkDialogProps) => {
    const popoverContent = useMemo(
        () => (
            <LinkDialogContent
                onSave={onSave}
                text={text}
                url={url}
                isOpen={isOpen}
                onVisibleChange={onVisibleChange}
            />
        ),
        [onSave, text, url, isOpen, onVisibleChange]
    );

    return linkRef
        ? createPortal(
              <Popover
                  trigger="click"
                  placement="bottom"
                  destroyTooltipOnHide={true}
                  overlayClassName={classnames('rain-link-dialog', !showArrow && 'no-arrow')}
                  visible={isOpen}
                  onVisibleChange={onVisibleChange}
                  content={popoverContent}>
                  <span style={{ height: '1em' }} />
              </Popover>,
              linkRef
          )
        : null;
};

const LinkDialogContent = ({
    text,
    url,
    isOpen,
    onSave,
    onVisibleChange,
}: {
    text: string;
    url: string;
    isOpen: boolean;
    onSave: (link: { text: string; url: string }) => void;
    onVisibleChange: (visible: boolean) => void;
}) => {
    const textRef = useRef<InputRef>(null);
    const urlRef = useRef<InputRef>(null);
    const hasText = Boolean(text);

    useEffect(() => {
        if (isOpen) {
            if (hasText) {
                urlRef.current?.focus();
            } else {
                textRef.current?.focus();
            }
        }
    }, [isOpen, hasText]);

    return (
        <div>
            <Form
                size="small"
                className="rain-link-dialog-content"
                onFinish={(values) => {
                    onSave({ text: values.text, url: values.url });
                }}>
                <Form.Item
                    rules={[{ required: true, message: 'Required' }]}
                    label="Text"
                    name="text"
                    initialValue={text}>
                    <Input ref={textRef} />
                </Form.Item>
                <Form.Item
                    rules={[
                        { required: true, message: 'Required' },
                        {
                            validator: (_, value) => {
                                // using validator.isUrl instead of type: 'url' because we want to
                                // make the protocol not required in the url.
                                if (!validator.isURL(value, { require_protocol: false })) {
                                    return Promise.reject(new Error('Should be a valid URL'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                    label="URL"
                    name="url"
                    initialValue={url}>
                    <Input ref={urlRef} />
                </Form.Item>
                <div className="rain-link-dialog-buttons">
                    <Button type="primary" htmlType="submit" size="small" title="Save">
                        <FontAwesomeIcon icon={faCheck} />
                    </Button>
                    <Button size="small" onClick={() => onVisibleChange(false)} title="Cancel">
                        <FontAwesomeIcon icon={faBan} />
                    </Button>
                </div>
            </Form>
        </div>
    );
};
